/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ORDER_STATUS_CONFIG, SHIPMENT_STATUS_CONFIG } from '../constants';

// =============================================================================
// Badges de status da transferência. Mesmo StatusPill do ClienteStatusBadge /
// ItemStatusBadge do Routing — a cor vem do mapa de configuração do módulo
// (constants.js), nunca hardcoded no call site.
// =============================================================================

const StatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   border: 1px solid ${({ $color }) => $color ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $bg }) => $bg ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $text }) => $text ?? '#444'};
   white-space: nowrap;
`;

function ConfigBadge({ value, config, onDark = false }) {
   // Status desconhecido (backend à frente do front) degrada para o valor cru
   // em vez de sumir da tela.
   const cfg = config[value] ?? {
      label: value ?? '—',
      description: null,
      bg: null,
      border: null,
      text: null,
      solidBg: null,
   };

   // No header ESCURO da página o pastel não atinge contraste — a pílula
   // inverte para o fill opaco (`solidBg`) com texto branco (UI_SPEC 2026-09-10).
   const isSolid = onDark && cfg.solidBg;

   return (
      <StatusPill
         $bg={isSolid ? cfg.solidBg : cfg.bg}
         $color={isSolid ? 'transparent' : cfg.border}
         $text={isSolid ? '#fff' : cfg.text}
         title={cfg.description ?? undefined}
      >
         {cfg.label}
      </StatusPill>
   );
}

ConfigBadge.propTypes = {
   value: PropTypes.string,
   // eslint-disable-next-line react/forbid-prop-types
   config: PropTypes.object.isRequired,
   onDark: PropTypes.bool,
};

export function OrderStatusBadge({ value, onDark = false }) {
   return (
      <ConfigBadge value={value} config={ORDER_STATUS_CONFIG} onDark={onDark} />
   );
}

OrderStatusBadge.propTypes = {
   value: PropTypes.string,
   onDark: PropTypes.bool,
};

export function ShipmentStatusBadge({ value, onDark = false }) {
   return (
      <ConfigBadge
         value={value}
         config={SHIPMENT_STATUS_CONFIG}
         onDark={onDark}
      />
   );
}

ShipmentStatusBadge.propTypes = {
   value: PropTypes.string,
   onDark: PropTypes.bool,
};

// Marca a ordem/remessa cuja divergência ainda não foi tratada pelo gestor.
// Só aparece onde o backend computa o dado de verdade: detalhe da ordem e
// respostas de mutação. Na LISTAGEM `has_pending_divergence` vem `null`
// (remessas não carregadas), então lá o badge não é renderizado.
const PendingPill = styled(StatusPill)`
   background: ${({ $onDark }) =>
      $onDark ? 'rgba(217,119,6,0.92)' : 'rgba(245,158,11,0.16)'};
   border-color: transparent;
   color: ${({ $onDark }) => ($onDark ? '#fff' : '#8a5a00')};
`;

export function PendingDivergenceBadge({ onDark = false }) {
   return (
      <PendingPill
         $onDark={onDark}
         title="Há divergência de conferência sem tratativa registrada."
      >
         Tratativa pendente
      </PendingPill>
   );
}

PendingDivergenceBadge.propTypes = { onDark: PropTypes.bool };

// Pílula da rota no header escuro do detalhe ("Origem → Destino"). Informativa
// (não navega); o fill sólido segue a mesma regra de contraste do onDark.
const RoutePillRoot = styled(StatusPill)`
   background: rgba(64, 152, 239, 0.92);
   border-color: transparent;
   color: #fff;
   font-weight: 700;
`;

export function RoutePill({ originName, destName }) {
   return (
      <RoutePillRoot title="PDV de origem → PDV de destino">
         {originName ?? '—'} → {destName ?? '—'}
      </RoutePillRoot>
   );
}

RoutePill.propTypes = {
   originName: PropTypes.string,
   destName: PropTypes.string,
};
